import { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Pencil, Trash2, Zap, Mail, MessageSquare, Clock, AlertCircle, Phone, Mic, FileText, RefreshCw, Copy, Eye, Send, History, ChevronDown, ChevronRight, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import DOMPurify from 'dompurify';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@easyflow/ui-frontend/components/ui/dialog';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { apiClient, formatApiErrorMessage } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useSocketOptional } from '@/contexts/SocketContext';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@easyflow/ui-frontend/components/ui/collapsible';
import { getErrorMessage, getApiErrorMessage } from '../../lib/utils';
import {
  appointmentAutomationSchema, appointmentAutomationInputSchema,
  appointmentAutomationPreviewResultSchema, appointmentAutomationPromptResultSchema,
  appointmentAutomationTestResultSchema,
  appointmentAutomationDraftPreviewResultSchema,
  appointmentAutomationTestStatusSchema, appointmentAutomationTestSendSchema,
  appointmentAutomationPreviewAppointmentSchema,
  appointmentAutomationVoiceStatsSchema,
} from '@easyflow/shared';
import type {
  AppointmentAutomation as Automation,
  AppointmentAutomationInput as AutomationPayload,
  AppointmentAutomationForm as AutomationForm,
  AppointmentAutomationPreviewResult,
  AppointmentAutomationPromptResult,
  AppointmentAutomationDraftPreviewResult,
  AppointmentAutomationTestSend as TestSendEntry,
  AppointmentAutomationPreviewAppointment,
  AppointmentAutomationVoiceStats,
  AppointmentAutomationWhatsAppTemplateOption,
  AppointmentAutomationEmailTemplateOption,
} from '@easyflow/shared';


interface ServiceType {
  id: string;
  name: string;
  color: string;
  is_active: boolean;
}

interface Sender {
  id: string;
  phone_number: string;
  channel: string;
  is_default: boolean;
  friendly_name: string | null;
}

interface EmailAccount {
  id: string;
  email_address: string;
  display_name: string | null;
  account_type: string;
  is_default: boolean;
}

const defaultForm: AutomationForm = {
  name: '',
  trigger_event: 'created',
  trigger_status: '',
  service_type_id: '',
  channel: 'whatsapp',
  template_id: '',
  template_name: '',
  message_body: '',
  email_subject: '',
  use_template: true,
  delay_minutes: 0,
  reminder_hours_before: 1,
  is_active: true,
  priority: 0,
  sender_id: '',
  sender_display: '',
  attach_ics: false,
  whatsapp_template_id: '',
  recipient: 'customer',
  voice_script: 'Buongiorno {{customer_name}}, la chiamo per ricordarle l\'appuntamento del {{appointment_date}} alle {{appointment_time}}. Può confermarlo, annullarlo o spostarlo. Mi dica pure.',
  voice_language: 'it',
  voice_provider: 'openai',
  voice_max_attempts: 2,
  voice_press_to_confirm: false,
  voice_agent_id: '',
  voice_trunk_id: '',
};

const triggerLabels: Record<string, string> = {
  created: 'Appuntamento creato',
  status_changed: 'Cambio stato',
  rescheduled: 'Riprogrammato',
  reminder: 'Promemoria',
};

const statusLabels: Record<string, string> = {
  scheduled: 'Programmato',
  confirmed: 'Confermato',
  cancelled: 'Annullato',
  completed: 'Completato',
  no_show: 'Non presentato',
};

const channelLabels: Record<string, string> = {
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  email: 'Email',
  gmail: 'Email (Gmail)',
  voice: 'Chiamata vocale AI',
};

const channelIcons: Record<string, LucideIcon> = {
  whatsapp: MessageSquare,
  sms: MessageSquare,
  email: Mail,
  gmail: Mail,
  voice: Phone,
};

interface Props {
  serviceTypes: ServiceType[];
}

const TEST_RECIPIENT_STORAGE_PREFIX = 'appointmentAutomations:testRecipient';

const getTestRecipientStorageKey = (
  userId: string | null | undefined,
  kind: 'phone' | 'email'
) => `${TEST_RECIPIENT_STORAGE_PREFIX}:${userId || 'anonymous'}:${kind}`;

const readStoredTestRecipient = (
  userId: string | null | undefined,
  kind: 'phone' | 'email'
): string => {
  if (typeof window === 'undefined') return '';
  try {
    return window.localStorage.getItem(getTestRecipientStorageKey(userId, kind)) || '';
  } catch {
    return '';
  }
};

const writeStoredTestRecipient = (
  userId: string | null | undefined,
  kind: 'phone' | 'email',
  value: string
) => {
  if (typeof window === 'undefined') return;
  try {
    const key = getTestRecipientStorageKey(userId, kind);
    if (value) {
      window.localStorage.setItem(key, value);
    } else {
      window.localStorage.removeItem(key);
    }
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
};

export default function AppointmentAutomations({ serviceTypes }: Props) {
  const { toast } = useToast();
  const { user } = useAuth();
  const userId = user?.id || null;
  const socketCtx = useSocketOptional();
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AutomationForm>({ ...defaultForm });
  const [waTemplates, setWaTemplates] = useState<AppointmentAutomationWhatsAppTemplateOption[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<AppointmentAutomationEmailTemplateOption[]>([]);
  const [senders, setSenders] = useState<Sender[]>([]);
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [voiceAgents, setVoiceAgents] = useState<Array<{ id: string; name: string; language?: string; provider_name?: string; provider_key?: string; is_active?: boolean }>>([]);
  const [voiceTrunks, setVoiceTrunks] = useState<Array<{ id: string; name: string; is_default?: boolean; is_active?: boolean }>>([]);
  const [showVoiceWarning, setShowVoiceWarning] = useState(false);
  const [pendingSavePayload, setPendingSavePayload] = useState<AutomationPayload | null>(null);
  const [testPhone, setTestPhone] = useState<string>(() => readStoredTestRecipient(userId, 'phone'));
  const [testEmail, setTestEmail] = useState<string>(() => readStoredTestRecipient(userId, 'email'));
  const [testingId, setTestingId] = useState<string | null>(null);
  const [sendingTestMessage, setSendingTestMessage] = useState(false);
  // Task #426: dialog inline per inserire/confermare il destinatario di test
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testDialogKind, setTestDialogKind] = useState<'phone' | 'email' | 'voice'>('phone');
  const [testDialogAutomationId, setTestDialogAutomationId] = useState<string | null>(null);
  const [testDialogChannel, setTestDialogChannel] = useState<string>('whatsapp');
  const [testDialogValue, setTestDialogValue] = useState<string>('');
  const [testDialogError, setTestDialogError] = useState<string | null>(null);
  // Task #427: stato di consegna del test message inviato dall'editor.
  // Per SMS/WhatsApp polliamo Twilio via il SID; per email mostriamo solo
  // "Accettato" perché non c'è tracking di consegna lato SMTP/Gmail.
  const [testStatus, setTestStatus] = useState<{
    channel: string;
    recipient: string;
    sid: string | null;
    status: string;
    errorCode: string | null;
    errorMessage: string | null;
    sentAt: number;
    polling: boolean;
  } | null>(null);
  const [voiceStats, setVoiceStats] = useState<AppointmentAutomationVoiceStats | null>(null);

  // Task #387: anteprima del prompt voce composto
  const [previewAuto, setPreviewAuto] = useState<Automation | null>(null);
  const [previewAppointments, setPreviewAppointments] = useState<AppointmentAutomationPreviewAppointment[]>([]);
  const [previewSelectedId, setPreviewSelectedId] = useState<string>('');
  const [previewManualId, setPreviewManualId] = useState<string>('');
  const [previewLoadingAppts, setPreviewLoadingAppts] = useState(false);
  const [previewLoadingPrompt, setPreviewLoadingPrompt] = useState(false);
  const [previewResult, setPreviewResult] = useState<
    ({ kind: 'voice' } & AppointmentAutomationPromptResult) |
    ({ kind: 'message' } & AppointmentAutomationPreviewResult) | null
  >(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBodyMode, setPreviewBodyMode] = useState<'rendered' | 'source'>('rendered');

  // Task #397: anteprima del messaggio renderizzato (SMS/Email/WhatsApp)
  const [msgPreviewOpen, setMsgPreviewOpen] = useState(false);
  const [msgPreviewAppts, setMsgPreviewAppts] = useState<AppointmentAutomationPreviewAppointment[]>([]);
  const [msgPreviewSelectedId, setMsgPreviewSelectedId] = useState<string>('');
  const [msgPreviewLoadingAppts, setMsgPreviewLoadingAppts] = useState(false);
  const [msgPreviewLoading, setMsgPreviewLoading] = useState(false);
  const [msgPreviewError, setMsgPreviewError] = useState<string | null>(null);
  const [msgPreviewResult, setMsgPreviewResult] = useState<AppointmentAutomationDraftPreviewResult | null>(null);

  // Task #425: mini-cronologia degli ultimi invii di test ("Ultimi test")
  // mostrata nella dialog di anteprima messaggio.
  const [testSends, setTestSends] = useState<TestSendEntry[]>([]);
  const [testSendsLoading, setTestSendsLoading] = useState(false);
  const [testSendsOpen, setTestSendsOpen] = useState(false);
  const [testSendsAutomationId, setTestSendsAutomationId] = useState<string | null>(null);
  // Task #445: stato di "rinvio" per riga (id dell'entry attualmente in invio)
  const [resendingEntryId, setResendingEntryId] = useState<string | null>(null);

  const loadAutomations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get<unknown>('/calendar/automations');
      setAutomations(appointmentAutomationSchema.array().parse(res.data));
    } catch (err) {
      toast({ title: 'Errore', description: 'Impossibile caricare le automazioni', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadTemplates = useCallback(async () => {
    try {
      const [waRes, emailRes, sendersRes, voiceRes] = await Promise.all([
        apiClient.get<AppointmentAutomationWhatsAppTemplateOption[]>('/messaging/templates/for-workflows').catch(() => ({ data: [] })),
        apiClient.get<AppointmentAutomationEmailTemplateOption[]>('/email/templates').catch(() => ({ data: [] })),
        apiClient.get<{ senders?: Sender[]; emailAccounts?: EmailAccount[] }>('/calendar/automations/senders').catch(() => ({ data: { senders: [], emailAccounts: [] } })),
        apiClient.get<{ agents?: typeof voiceAgents; trunks?: typeof voiceTrunks }>('/calendar/automations/voice-agents').catch(() => ({ data: { agents: [], trunks: [] } })),
      ]);
      setWaTemplates(waRes.data || []);
      setEmailTemplates(emailRes.data || []);
      const sendersData = (sendersRes.data) || {};
      setSenders(sendersData.senders || []);
      setEmailAccounts(sendersData.emailAccounts || []);
      const voiceData = (voiceRes.data) || {};
      setVoiceAgents(voiceData.agents || []);
      setVoiceTrunks(voiceData.trunks || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  }, []);

  const loadVoiceStats = useCallback(async () => {
    try {
      const r = await apiClient.get<unknown>('/calendar/automations/voice-stats');
      setVoiceStats(appointmentAutomationVoiceStatsSchema.parse(r.data));
    } catch {
      // optional
    }
  }, []);

  useEffect(() => {
    loadAutomations();
    loadTemplates();
    loadVoiceStats();
  }, [loadAutomations, loadTemplates, loadVoiceStats]);

  // Task #426: hydrate the remembered test recipients when the active user changes
  useEffect(() => {
    setTestPhone(readStoredTestRecipient(userId, 'phone'));
    setTestEmail(readStoredTestRecipient(userId, 'email'));
  }, [userId]);

  // Task #426: persist the last-used test recipients per logged-in user
  useEffect(() => {
    writeStoredTestRecipient(userId, 'phone', testPhone);
  }, [userId, testPhone]);

  useEffect(() => {
    writeStoredTestRecipient(userId, 'email', testEmail);
  }, [userId, testEmail]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...defaultForm });
    setTestStatus(null);
    setShowDialog(true);
  };

  const openEdit = (auto: Automation) => {
    setEditingId(auto.id);
    setTestStatus(null);
    const hasTemplate = !!auto.template_id;
    const hasIcs = Array.isArray(auto.attachments) && auto.attachments.some(a => a?.type === 'ics');
    const rawRecipient = auto.conditions?.recipient;
    const recipient = (typeof rawRecipient === 'string' ? rawRecipient : '') || 'customer';
    setForm({
      name: auto.name,
      trigger_event: auto.trigger_event,
      trigger_status: auto.trigger_status || '',
      service_type_id: auto.service_type_id || '',
      channel: auto.channel === 'email' && emailAccounts.find(account => account.id === auto.sender_id)?.account_type === 'gmail' ? 'gmail' : auto.channel,
      template_id: auto.template_id || '',
      template_name: auto.template_name || '',
      message_body: auto.message_body || '',
      email_subject: auto.email_subject || '',
      use_template: hasTemplate,
      delay_minutes: auto.delay_minutes,
      reminder_hours_before: auto.reminder_hours_before,
      is_active: auto.is_active,
      priority: auto.priority,
      sender_id: auto.sender_id || '',
      sender_display: auto.sender_display || '',
      attach_ics: hasIcs,
      whatsapp_template_id: auto.whatsapp_template_id || '',
      recipient,
      voice_script: auto.voice_script || defaultForm.voice_script,
      voice_language: auto.voice_language || 'it',
      voice_provider: auto.voice_provider || 'openai',
      voice_max_attempts: auto.voice_max_attempts ?? 2,
      voice_press_to_confirm: !!auto.voice_press_to_confirm,
      voice_agent_id: auto.voice_agent_id || '',
      voice_trunk_id: auto.voice_trunk_id || '',
    });
    setShowDialog(true);
  };

  const persistAutomation = async (payload: AutomationPayload) => {
    try {
      if (editingId) {
        await apiClient.put(`/calendar/automations/${editingId}`, payload);
        toast({ title: 'Salvato', description: 'Automazione aggiornata' });
      } else {
        await apiClient.post('/calendar/automations', payload);
        toast({ title: 'Creato', description: 'Automazione creata' });
      }
      setShowDialog(false);
      loadAutomations();
      loadVoiceStats();
    } catch (err) {
      toast({ title: 'Errore', description: formatApiErrorMessage(err, 'Impossibile salvare'), variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Errore', description: 'Inserisci un nome', variant: 'destructive' });
      return;
    }

    try {
      const attachments: Array<{ type: string }> = [];
      if ((form.channel === 'email' || form.channel === 'gmail') && form.attach_ics) attachments.push({ type: 'ics' });

      const payload = appointmentAutomationInputSchema.parse({
        name: form.name,
        trigger_event: form.trigger_event,
        trigger_status: form.trigger_event === 'status_changed' ? form.trigger_status || null : null,
        service_type_id: form.service_type_id || null,
        channel: form.channel === 'gmail' ? 'email' : form.channel,
        template_id: form.use_template ? (form.template_id || null) : null,
        template_name: form.use_template ? (form.template_name || null) : null,
        message_body: form.use_template ? null : (form.message_body || null),
        email_subject: form.use_template ? null : (form.email_subject || null),
        sender_id: form.sender_id || null,
        sender_display: form.sender_display || null,
        delay_minutes: form.delay_minutes,
        reminder_hours_before: form.trigger_event === 'reminder' ? form.reminder_hours_before : null,
        is_active: form.is_active,
        priority: form.priority,
        attachments,
        whatsapp_template_id: form.channel === 'whatsapp' ? (form.whatsapp_template_id || null) : null,
        conditions: { recipient: form.recipient || 'customer' },
        voice_script: form.channel === 'voice' ? form.voice_script : null,
        voice_language: form.channel === 'voice' ? form.voice_language : null,
        // Provider derivato dall'agente selezionato (l'agente porta il proprio
        // provider). Se nessun agente selezionato, fallback al provider del form.
        voice_provider: form.channel === 'voice'
          ? (voiceAgents.find(a => a.id === form.voice_agent_id)?.provider_key || form.voice_provider || 'openai')
          : null,
        voice_max_attempts: form.channel === 'voice' ? form.voice_max_attempts : null,
        // DTMF rimosso dall'UI: la gestione conferma/annulla/sposta è
        // interamente conversazionale tramite gli MCP tools dell'agente.
        voice_press_to_confirm: form.channel === 'voice' ? false : null,
        voice_agent_id: form.channel === 'voice' ? (form.voice_agent_id || null) : null,
        voice_trunk_id: form.channel === 'voice' ? (form.voice_trunk_id || null) : null,
      });

      // Opt-in warning the first time the user activates a voice automation
      if (form.channel === 'voice' && form.is_active && !localStorage.getItem('voice_automation_optin_ack')) {
        setPendingSavePayload(payload);
        setShowVoiceWarning(true);
        return;
      }

      await persistAutomation(payload);
    } catch (err) {
      toast({ title: 'Errore', description: formatApiErrorMessage(err, 'Impossibile salvare'), variant: 'destructive' });
    }
  };

  const confirmVoiceOptin = async () => {
    localStorage.setItem('voice_automation_optin_ack', new Date().toISOString());
    setShowVoiceWarning(false);
    if (pendingSavePayload) {
      await persistAutomation(pendingSavePayload);
      setPendingSavePayload(null);
    }
  };

  // Task #433: dialog inline per "Test chiamata" con numero ricordato (riusa il
  // dialog del test SMS/WhatsApp/Email, kind='voice').
  const handleTestVoice = (autoId: string) => {
    setTestDialogAutomationId(autoId);
    setTestDialogKind('voice');
    setTestDialogChannel('voice');
    setTestDialogValue(testPhone);
    setTestDialogError(null);
    setTestDialogOpen(true);
  };

  // Task #407 / #426: apre un dialog inline che pre-compila l'ultimo destinatario
  // di test usato (memorizzato per utente). L'operatore può confermare o sovrascrivere
  // il valore prima dell'invio. Disponibile solo dopo aver salvato l'automazione.
  const handleSendTestMessage = () => {
    if (!editingId) {
      toast({
        title: 'Salva prima di testare',
        description: 'Salva l\'automazione per poter inviare un messaggio di test.',
        variant: 'destructive',
      });
      return;
    }
    const isEmail = form.channel === 'email' || form.channel === 'gmail';
    setTestDialogAutomationId(editingId);
    setTestDialogChannel(form.channel);
    setTestDialogError(null);
    if (isEmail) {
      setTestDialogKind('email');
      setTestDialogValue(testEmail);
    } else {
      setTestDialogKind('phone');
      setTestDialogValue(testPhone);
    }
    setTestDialogOpen(true);
  };

  // Task #425: ricarica gli ultimi invii di test per l'automazione corrente.
  const loadTestSends = useCallback(async (automationId: string) => {
    if (!automationId) {
      setTestSends([]);
      return;
    }
    try {
      setTestSendsLoading(true);
      const res = await apiClient.get<unknown>(`/calendar/automations/${automationId}/test-sends?limit=20`);
      const rows = appointmentAutomationTestSendSchema.array().parse(res.data);
      setTestSends(Array.isArray(rows) ? rows : []);
    } catch (err) {
      // Silenzioso: la cronologia è un nice-to-have, non blocca la dialog.
      console.warn('Failed to load automation test sends:', err);
      setTestSends([]);
    } finally {
      setTestSendsLoading(false);
    }
  }, []);

  const submitTestMessage = async () => {
    const targetId = testDialogAutomationId;
    if (!targetId) return;
    const trimmed = testDialogValue.trim();
    if (!trimmed) {
      setTestDialogError('Il campo non può essere vuoto');
      return;
    }
    if (testDialogKind === 'email') {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        setTestDialogError('Inserisci un indirizzo email corretto');
        return;
      }
      setTestEmail(trimmed);
    } else {
      // Normalizzazione tipo Twilio: accetta 3331234567, +393331234567,
      // 393331234567, 003933... → tutto convertito a E.164 lato server.
      const onlyDigits = trimmed.replace(/[\s\-().]/g, '');
      const looksValid = /^\+?[0-9]{7,16}$/.test(onlyDigits);
      if (!looksValid) {
        setTestDialogError('Numero non valido. Inserisci un numero italiano (es. 3331234567) o internazionale (es. +393331234567).');
        return;
      }
      setTestPhone(trimmed);
    }
    setTestDialogError(null);
    try {
      setSendingTestMessage(true);
      if (testDialogKind === 'voice') {
        setTestingId(targetId);
        try {
          await apiClient.post(`/calendar/automations/${targetId}/test-voice`, { test_phone: trimmed });
          toast({ title: 'Chiamata di test avviata', description: `Stiamo chiamando ${trimmed}` });
        } finally {
          setTestingId(null);
        }
      } else {
        // Task #427: reset previous badge, send, then start polling Twilio for SMS/WA SID.
        setTestStatus(null);
        const payload = testDialogKind === 'email'
          ? { test_email: trimmed }
          : { test_phone: trimmed };
        const res = await apiClient.post<unknown>(`/calendar/automations/${targetId}/test-message`, { ...payload, use_fictitious: true });
        const data = appointmentAutomationTestResultSchema.parse(res.data);
        const sid = data.message_sid || null;
        // Email: no SID → acceptance only. SMS/WhatsApp: SID → polling.
        setTestStatus({
          channel: data.channel || form.channel,
          recipient: data.recipient || trimmed,
          sid,
          status: data.dispatch?.status ?? 'accepted',
          errorCode: null,
          errorMessage: data.dispatch && 'reason' in data.dispatch ? data.dispatch.reason : null,
          sentAt: Date.now(),
          polling: !!sid,
        });
        const dispatch = data.dispatch;
        const description = dispatch && 'reason' in dispatch
          ? dispatch.reason
          : dispatch?.status === 'delivered'
            ? `Consegna confermata per ${trimmed}.`
            : `Richiesta accettata per ${trimmed}. La consegna non è ancora confermata.`;
        toast({
          title: dispatch && 'reason' in dispatch ? 'Esito test da verificare' : dispatch?.status === 'delivered' ? 'Test consegnato' : 'Test accettato',
          description,
          variant: dispatch && 'reason' in dispatch ? 'destructive' : 'default',
        });
      }
      setTestDialogOpen(false);
      // Task #425: aggiorna la mini-cronologia se la dialog di anteprima è aperta
      if (editingId) {
        loadTestSends(editingId);
      }
    } catch (err) {
      const message = getApiErrorMessage(err) || getErrorMessage(err) || 'Errore durante l\'invio';
      setTestDialogError(message);
      setTestStatus({
        channel: form.channel,
        recipient: trimmed,
        sid: null,
        status: 'failed',
        errorCode: null,
        errorMessage: message,
        sentAt: Date.now(),
        polling: false,
      });
      toast({
        title: 'Invio fallito',
        description: message,
        variant: 'destructive',
      });
      // Anche un test fallito è registrato lato backend: refresh per mostrarlo
      if (editingId) {
        loadTestSends(editingId);
      }
    } finally {
      setSendingTestMessage(false);
    }
  };

  // Task #427: poll Twilio for the delivery status of the latest test message.
  // Stops on terminal states (delivered/read/failed/undelivered) or after 90s.
  const TERMINAL_TEST_STATUSES = useMemo(() => (['delivered', 'read', 'received', 'failed', 'undelivered']), []);
  useEffect(() => {
    if (!testStatus || !testStatus.polling || !testStatus.sid) return;
    if (TERMINAL_TEST_STATUSES.includes(testStatus.status)) return;
    const elapsed = Date.now() - testStatus.sentAt;
    if (elapsed > 90_000) {
      setTestStatus(s => s ? { ...s, polling: false } : s);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await apiClient.get<unknown>(
          `/calendar/automations/test-message-status?sid=${encodeURIComponent(testStatus.sid || '')}`
        );
        const data = appointmentAutomationTestStatusSchema.parse(res.data);
        const newStatus = String(data.status || 'unknown').toLowerCase();
        const isTerminal = TERMINAL_TEST_STATUSES.includes(newStatus);
        setTestStatus(s => s && s.sid === testStatus.sid ? {
          ...s,
          status: newStatus,
          errorCode: data.error_code || null,
          errorMessage: data.error_message || null,
          polling: !isTerminal,
        } : s);
      } catch (err) {
        // Errore temporaneo: continua a pollare finché non scade il timeout.
        console.warn('[test-status] poll failed:', getErrorMessage(err));
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [testStatus, TERMINAL_TEST_STATUSES]);

  // Helper for the inline delivery badge shown next to "Invia test a me".
  const renderTestStatusBadge = () => {
    if (!testStatus) return null;
    const s = testStatus.status;
    let label = '';
    let tone: 'info' | 'success' | 'error' | 'pending' = 'pending';
    let Icon: LucideIcon = Loader2;
    let spin = false;
    switch (s) {
      case 'queued':
        label = 'In coda'; tone = 'pending'; spin = true; break;
      case 'accepted':
        label = 'Accettato (consegna non confermata)'; tone = 'info'; break;
      case 'scheduled':
        label = 'Programmato'; tone = 'pending'; spin = true; break;
      case 'sending':
        label = 'Invio in corso'; tone = 'pending'; spin = true; break;
      case 'sent':
        label = 'Inviato al provider (consegna non confermata)'; tone = 'info'; Icon = Send; break;
      case 'delivered':
        label = 'Consegnato'; tone = 'success'; Icon = CheckCircle2; break;
      case 'read':
        label = 'Letto'; tone = 'success'; Icon = CheckCircle2; break;
      case 'received':
        label = 'Ricevuto'; tone = 'success'; Icon = CheckCircle2; break;
      case 'failed':
      case 'retryable_failure':
      case 'permanent_failure':
        label = 'Errore'; tone = 'error'; Icon = XCircle; break;
      case 'skipped':
        label = 'Non inviato'; tone = 'info'; break;
      case 'uncertain':
        label = 'Esito incerto (non reinviare automaticamente)'; tone = 'info'; break;
      case 'undelivered':
        label = 'Non consegnato'; tone = 'error'; Icon = XCircle; break;
      default:
        label = s ? s.charAt(0).toUpperCase() + s.slice(1) : 'In attesa';
        tone = 'pending'; spin = true; break;
    }
    const toneClass =
      tone === 'success' ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300' :
      tone === 'error' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300' :
      tone === 'info' ? 'border-sky-200 dark:border-sky-800 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300' :
                        'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300';
    const errorDetail = (s === 'failed' || s === 'undelivered')
      ? (testStatus.errorMessage || (testStatus.errorCode ? `Codice ${testStatus.errorCode}` : 'Verifica numero/indirizzo destinatario o credito Twilio.'))
      : null;
    return (
      <div className={`mt-1 inline-flex flex-col gap-0.5 rounded-md border px-2 py-1 text-[10px] ${toneClass}`}>
        <div className="inline-flex items-center gap-1">
          <Icon className={`w-3 h-3 ${spin ? 'animate-spin' : ''}`} />
          <span className="font-medium">Stato test:</span>
          <span>{label}</span>
          {testStatus.recipient && (
            <span className="opacity-70">→ {testStatus.recipient}</span>
          )}
        </div>
        {errorDetail && (
          <div className="leading-snug">{errorDetail}</div>
        )}
      </div>
    );
  };

  const openPreviewPrompt = async (auto: Automation) => {
    setPreviewAuto(auto);
    setPreviewResult(null);
    setPreviewError(null);
    setPreviewSelectedId('');
    setPreviewManualId('');
    setPreviewAppointments([]);
    setPreviewLoadingAppts(true);
    try {
      const now = new Date();
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const end = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
      });
      if (auto.service_type_id) params.set('type', auto.service_type_id);
      const res = await apiClient.get<unknown>(`/calendar/appointments?${params.toString()}`);
      const list = appointmentAutomationPreviewAppointmentSchema.array().parse(res.data);
      const sorted = [...list].sort((a, b) => {
        const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
        const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
        return tb - ta;
      }).slice(0, 50);
      setPreviewAppointments(sorted);
    } catch (err) {
      console.error('Failed to load appointments for preview:', err);
    } finally {
      setPreviewLoadingAppts(false);
    }
  };

  const runPreviewPrompt = async () => {
    if (!previewAuto) return;
    const appointmentId = (previewManualId.trim() || previewSelectedId).trim();
    if (!appointmentId) {
      setPreviewError('Seleziona un appuntamento o inserisci un ID');
      return;
    }
    setPreviewError(null);
    setPreviewLoadingPrompt(true);
    setPreviewResult(null);
    setPreviewBodyMode('rendered');
    try {
      if (previewAuto.channel === 'voice') {
        const res = await apiClient.get<unknown>(
          `/calendar/automations/${previewAuto.id}/preview-prompt?appointment_id=${encodeURIComponent(appointmentId)}`
        );
        const data = appointmentAutomationPromptResultSchema.parse(res.data);
        if (!data?.prompt) {
          setPreviewError('Risposta non valida dal server');
          return;
        }
        setPreviewResult({
          kind: 'voice',
          prompt: data.prompt,
          length: data.length ?? data.prompt.length,
          hash: data.hash || '',
          trigger: data.trigger || previewAuto.trigger_event,
          language: data.language || 'it',
        });
      } else {
        const res = await apiClient.get<unknown>(
          `/calendar/automations/${previewAuto.id}/preview-message?appointment_id=${encodeURIComponent(appointmentId)}`
        );
        const data = appointmentAutomationPreviewResultSchema.parse(res.data);
        if (!data || (typeof data.body !== 'string' && !data.body_html)) {
          setPreviewError('Risposta non valida dal server');
          return;
        }
        setPreviewResult({
          ...data,
          kind: 'message',
          channel: data.channel || previewAuto.channel,
          subject: data.subject ?? null,
          body: data.body || '',
          body_html: data.body_html || null,
          body_format: data.body_format || 'text',
          template_name: data.template_name || null,
          sender_display: data.sender_display || null,
          recipient: data.recipient || null,
          recipient_address: data.recipient_address || null,
          has_ics_attachment: !!data.has_ics_attachment,
          length: data.length || (data.body || '').length,
        });
      }
    } catch (err) {
      setPreviewError(getApiErrorMessage(err) || getErrorMessage(err) || 'Errore generazione anteprima');
    } finally {
      setPreviewLoadingPrompt(false);
    }
  };

  const copyPreviewPrompt = async () => {
    if (!previewResult) return;
    let text = '';
    if (previewResult.kind === 'voice') {
      text = previewResult.prompt;
    } else {
      const parts: string[] = [];
      if (previewResult.subject) parts.push(`Oggetto: ${previewResult.subject}`);
      parts.push(previewResult.body_html || previewResult.body);
      text = parts.join('\n\n');
    }
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copiato', description: 'Anteprima copiata negli appunti' });
    } catch {
      toast({ title: 'Errore', description: 'Impossibile copiare', variant: 'destructive' });
    }
  };

  // Task #444: ascolta gli aggiornamenti di stato consegna inviati dal
  // webhook Twilio per aggiornare in real-time la mini-cronologia mentre
  // la dialog di anteprima è aperta.
  useEffect(() => {
    if (!socketCtx?.isConnected || !socketCtx.on || !socketCtx.off) return;
    if (!testSendsAutomationId) return;
    const handler = (data: {
      id?: string;
      automation_id?: string;
      provider_message_id?: string;
      delivery_status?: string;
      delivery_error_code?: string | null;
      delivery_error_message?: string | null;
      delivery_status_updated_at?: string;
    }) => {
      if (!data || data.automation_id !== testSendsAutomationId) return;
      setTestSends(prev => prev.map(entry => {
        const matchById = data.id && entry.id === data.id;
        const matchBySid = !matchById && data.provider_message_id
          && entry.provider_message_id === data.provider_message_id;
        if (!matchById && !matchBySid) return entry;
        return {
          ...entry,
          delivery_status: data.delivery_status ?? entry.delivery_status,
          delivery_error_code: data.delivery_error_code ?? entry.delivery_error_code,
          delivery_error_message: data.delivery_error_message ?? entry.delivery_error_message,
          delivery_status_updated_at: data.delivery_status_updated_at ?? entry.delivery_status_updated_at,
        };
      }));
    };
    socketCtx.on('automation:test_send_status_update', handler);
    return () => {
      socketCtx.off?.('automation:test_send_status_update', handler);
    };
  }, [socketCtx?.isConnected, socketCtx?.on, socketCtx?.off, testSendsAutomationId, socketCtx]);

  // Task #445: rinvia un test usando lo stesso destinatario di una riga della cronologia.
  // Riusa l'endpoint /test-message (o /test-voice per il canale voce) e ricarica la lista
  // così il nuovo tentativo appare immediatamente in cima.
  const resendTestFromHistory = useCallback(async (entry: TestSendEntry) => {
    const automationId = testSendsAutomationId;
    if (!automationId) return;
    const recipient = (entry.recipient || '').trim();
    if (!recipient) {
      toast({ title: 'Destinatario mancante', description: 'Questa riga non ha un destinatario valido.', variant: 'destructive' });
      return;
    }
    const channel = entry.channel;
    const isEmail = channel === 'email' || channel === 'gmail';
    const isVoice = channel === 'voice';
    setResendingEntryId(entry.id);
    try {
      if (isVoice) {
        await apiClient.post(`/calendar/automations/${automationId}/test-voice`, { test_phone: recipient });
        setTestPhone(recipient);
        toast({ title: 'Chiamata di test avviata', description: `Stiamo richiamando ${recipient}` });
      } else {
        setTestStatus(null);
        const payload = isEmail ? { test_email: recipient } : { test_phone: recipient };
        const res = await apiClient.post<unknown>(`/calendar/automations/${automationId}/test-message`, { ...payload, use_fictitious: true });
        const data = appointmentAutomationTestResultSchema.parse(res.data);
        const sid = data.message_sid || null;
        setTestStatus({
          channel: data.channel || channel,
          recipient: data.recipient || recipient,
          sid,
          status: data.dispatch?.status ?? 'accepted',
          errorCode: null,
          errorMessage: data.dispatch && 'reason' in data.dispatch ? data.dispatch.reason : null,
          sentAt: Date.now(),
          polling: !!sid,
        });
        if (isEmail) setTestEmail(recipient);
        else setTestPhone(recipient);
        toast({
          title: data.dispatch && 'reason' in data.dispatch ? 'Esito rinvio da verificare' : 'Rinvio accettato',
          description: data.dispatch && 'reason' in data.dispatch ? data.dispatch.reason
            : data.dispatch?.status === 'delivered' ? `Consegna confermata per ${recipient}.`
              : `Richiesta accettata per ${recipient}. Consegna non ancora confermata.`,
          variant: data.dispatch && 'reason' in data.dispatch ? 'destructive' : 'default',
        });
      }
      loadTestSends(automationId);
    } catch (err) {
      const message = getApiErrorMessage(err) || getErrorMessage(err) || 'Errore durante il rinvio';
      toast({ title: 'Rinvio fallito', description: message, variant: 'destructive' });
      loadTestSends(automationId);
    } finally {
      setResendingEntryId(null);
    }
  }, [testSendsAutomationId, toast, loadTestSends]);

  const runMessagePreview = async (appointmentIdOverride?: string) => {
    setMsgPreviewError(null);
    setMsgPreviewLoading(true);
    try {
      const isEmail = form.channel === 'email' || form.channel === 'gmail';
      const subject = isEmail ? (form.email_subject || null) : null;
      let body = form.message_body || '';
      // For WhatsApp / SMS template-mode, prefer the local template body_text
      // (already loaded in waTemplates) so the preview reflects what Twilio
      // will render even when message_body is empty.
      if (form.channel === 'whatsapp' && !body) {
        const tmpl = waTemplates.find(t => t.id === form.whatsapp_template_id || t.content_sid === form.template_id);
        if (tmpl?.body_text) body = tmpl.body_text;
      }
      if (form.channel === 'sms' && form.use_template && !body) {
        const tmpl = waTemplates.find(t => t.content_sid === form.template_id);
        if (tmpl?.body_text) body = tmpl.body_text;
      }
      // Email templates only carry name/subject in the local list; send
      // template_id so the backend resolves body+subject server-side.
      const templateId = isEmail && form.use_template ? (form.template_id || null) : null;
      if (!body && !subject && !templateId) {
        setMsgPreviewError('Inserisci un testo da renderizzare prima di generare l\'anteprima.');
        setMsgPreviewLoading(false);
        return;
      }
      const apptId = appointmentIdOverride !== undefined ? appointmentIdOverride : msgPreviewSelectedId;
      const res = await apiClient.post<AppointmentAutomationDraftPreviewResult>('/calendar/automations/preview-message', {
        appointment_id: apptId || null,
        body,
        subject,
        template_id: templateId,
        channel: form.channel === 'gmail' ? 'email' : form.channel,
        use_fictitious: !apptId,
        language: form.channel === 'voice' ? form.voice_language : 'it',
      });
      const data = appointmentAutomationDraftPreviewResultSchema.parse(res.data);
      if (!data) {
        setMsgPreviewError('Risposta non valida dal server');
        return;
      }
      setMsgPreviewResult({
        ...data,
        subject: data.subject ?? null,
        body: data.body || '',
        unresolved: Array.isArray(data.unresolved) ? data.unresolved : [],
        appointment: data.appointment || { id: '', title: null, start_time: null, customer_name: null },
        is_fictitious: !!data.is_fictitious,
      });
    } catch (err) {
      setMsgPreviewError(getApiErrorMessage(err) || getErrorMessage(err) || 'Errore generazione anteprima');
    } finally {
      setMsgPreviewLoading(false);
    }
  };

  const openMessagePreview = async () => {
    setMsgPreviewError(null);
    setMsgPreviewResult(null);
    setMsgPreviewSelectedId('');
    setMsgPreviewAppts([]);
    setMsgPreviewOpen(true);
    setMsgPreviewLoadingAppts(true);
    // Task #425: carica la mini-cronologia dei test in parallelo
    if (editingId) {
      setTestSendsAutomationId(editingId);
      loadTestSends(editingId);
    } else {
      setTestSendsAutomationId(null);
      setTestSends([]);
    }
    try {
      const now = new Date();
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const end = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString(),
      });
      if (form.service_type_id) params.set('type', form.service_type_id);
      const res = await apiClient.get<unknown>(`/calendar/appointments?${params.toString()}`);
      const list = appointmentAutomationPreviewAppointmentSchema.array().parse(res.data);
      const sorted = [...list].sort((a, b) => {
        const ta = a.start_time ? new Date(a.start_time).getTime() : 0;
        const tb = b.start_time ? new Date(b.start_time).getTime() : 0;
        return tb - ta;
      }).slice(0, 50);
      setMsgPreviewAppts(sorted);
      // Synthetic context is the default; real appointments require a choice.
      await runMessagePreview('');
    } catch (err) {
      console.error('Failed to load appointments for message preview:', err);
      // Still try to run preview with no appointment_id (synthetic fallback)
      await runMessagePreview('');
    } finally {
      setMsgPreviewLoadingAppts(false);
    }
  };

  const renderHighlightedPreview = (text: string, unresolved: string[]) => {
    if (!text) return <span className="text-muted-foreground italic">(vuoto)</span>;
    const unresolvedSet = new Set(unresolved);
    const parts: Array<{ text: string; isUnresolved: boolean }> = [];
    const re = /\{\{\s*([\w.]+)\s*\}\}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) parts.push({ text: text.slice(lastIndex, match.index), isUnresolved: false });
      parts.push({ text: match[0], isUnresolved: unresolvedSet.has(match[1]!) });
      lastIndex = re.lastIndex;
    }
    if (lastIndex < text.length) parts.push({ text: text.slice(lastIndex), isUnresolved: false });
    return parts.map((p, i) =>
      p.isUnresolved
        ? <mark key={i} className="bg-amber-200 dark:bg-amber-800/60 text-amber-900 dark:text-amber-100 rounded px-0.5">{p.text}</mark>
        : <span key={i}>{p.text}</span>
    );
  };

  const formatPreviewAppointmentLabel = (a: { customer_name?: string | null; start_time?: string | null; service_type_name?: string | null }) => {
    const parts: string[] = [];
    if (a.start_time) {
      try {
        const d = new Date(a.start_time);
        parts.push(d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }));
      } catch { /* noop */ }
    }
    if (a.customer_name) parts.push(a.customer_name);
    if (a.service_type_name) parts.push(a.service_type_name);
    return parts.length ? parts.join(' · ') : '(senza dettagli)';
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Eliminare questa automazione?')) return;
    try {
      await apiClient.delete(`/calendar/automations/${id}`);
      toast({ title: 'Eliminato', description: 'Automazione eliminata' });
      loadAutomations();
    } catch (err) {
      toast({ title: 'Errore', description: formatApiErrorMessage(err, 'Si è verificato un errore'), variant: 'destructive' });
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await apiClient.patch(`/calendar/automations/${id}/toggle`);
      loadAutomations();
    } catch (err) {
      toast({ title: 'Errore', description: formatApiErrorMessage(err, 'Si è verificato un errore'), variant: 'destructive' });
    }
  };

  const getChannelIcon = (channel: string) => {
    const Icon = channelIcons[channel] || MessageSquare;
    return <Icon className="w-3.5 h-3.5" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500 dark:text-amber-400" />
            Automazioni Appuntamenti
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Invio automatico messaggi su eventi appuntamento
          </p>
        </div>
        <Button size="sm" className="h-7 text-xs" onClick={openCreate}>
          <Plus className="w-3.5 h-3.5 mr-1" />
          Nuova
        </Button>
      </div>

      {voiceStats && voiceStats.total > 0 && (
        <div className="mb-3 p-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/40">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-violet-600 dark:text-violet-400" />
              <span className="text-xs font-medium text-violet-900 dark:text-violet-100">Statistiche chiamate vocali AI</span>
            </div>
            <span className="text-[10px] text-violet-700 dark:text-violet-300">{voiceStats.total} chiamate totali</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="p-2 rounded bg-card border border-border">
              <div className="text-base font-semibold text-emerald-600 dark:text-emerald-400">{voiceStats.confirmed_voice}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">Confermati</div>
              <div className="text-[10px] text-muted-foreground">
                {voiceStats.total ? Math.round((voiceStats.confirmed_voice / voiceStats.total) * 100) : 0}%
              </div>
            </div>
            <div className="p-2 rounded bg-card border border-border">
              <div className="text-base font-semibold text-rose-600 dark:text-rose-400">{voiceStats.cancelled_voice}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">Annullati</div>
              <div className="text-[10px] text-muted-foreground">
                {voiceStats.total ? Math.round((voiceStats.cancelled_voice / voiceStats.total) * 100) : 0}%
              </div>
            </div>
            <div className="p-2 rounded bg-card border border-border">
              <div className="text-base font-semibold text-amber-600 dark:text-amber-400">{voiceStats.rescheduled_voice}</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">Riprogrammati</div>
              <div className="text-[10px] text-muted-foreground">
                {voiceStats.total ? Math.round((voiceStats.rescheduled_voice / voiceStats.total) * 100) : 0}%
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2 text-[10px] text-muted-foreground">
            <span>Risposte: <strong className="text-foreground">{voiceStats.answered}</strong></span>
            <span>Non risposte: <strong className="text-foreground">{voiceStats.no_answer}</strong></span>
            <span>Fallite: <strong className="text-foreground">{voiceStats.failed}</strong></span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-6 text-muted-foreground text-sm">Caricamento...</div>
      ) : automations.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-input rounded-lg">
          <Zap className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Nessuna automazione configurata</p>
          <p className="text-xs text-muted-foreground mt-1">Crea la prima per inviare messaggi automatici</p>
        </div>
      ) : (
        <div className="space-y-2">
          {automations.map((auto) => (
            <div
              key={auto.id}
              className={`p-3 rounded-lg border ${auto.is_active ? 'border-border bg-card' : 'border-border bg-muted opacity-60'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-foreground truncate">{auto.name}</span>
                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 shrink-0">
                      {getChannelIcon(auto.channel)}
                      <span className="ml-1">{channelLabels[auto.channel]}</span>
                    </Badge>
                    {!auto.is_active && (
                      <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-muted text-muted-foreground">
                        Disattivata
                      </Badge>
                    )}
                    {auto.is_system_default && (
                      <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-primary/10 text-primary border-transparent">
                        Sistema
                      </Badge>
                    )}
                    {Array.isArray(auto.attachments) && auto.attachments.some(a => a?.type === 'ics') && (
                      <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300">
                        .ics
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Zap className="w-3 h-3" />
                      {triggerLabels[auto.trigger_event] || auto.trigger_event}
                    </span>
                    {auto.trigger_status && (
                      <span>→ {statusLabels[auto.trigger_status] || auto.trigger_status}</span>
                    )}
                    {auto.trigger_event === 'reminder' && auto.reminder_hours_before !== null && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {auto.reminder_hours_before}h prima
                      </span>
                    )}
                    {auto.delay_minutes > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        +{auto.delay_minutes}min
                      </span>
                    )}
                    {auto.service_type_name && (
                      <Badge variant="outline" className="text-[10px] h-4 px-1">
                        {auto.service_type_name}
                      </Badge>
                    )}
                  </div>
                  {(auto.sender_display || auto.executions_count > 0) && (
                    <div className="flex items-center gap-2 mt-1">
                      {auto.sender_display && (
                        <span className="text-[10px] text-muted-foreground">
                          Da: {auto.sender_display}
                        </span>
                      )}
                      {auto.executions_count > 0 && (
                        <span className="text-[10px] text-muted-foreground">
                          {auto.executions_count} esecuzioni
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-2 shrink-0">
                  <Switch
                    checked={auto.is_active}
                    onCheckedChange={() => handleToggle(auto.id)}
                    className="scale-75"
                  />
                  {auto.channel === 'voice' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Chiamata di test sul tuo numero"
                      disabled={testingId === auto.id}
                      onClick={() => handleTestVoice(auto.id)}
                    >
                      <Phone className={`w-3.5 h-3.5 ${testingId === auto.id ? 'text-violet-300 dark:text-violet-400' : 'text-violet-500 dark:text-violet-400'}`} />
                    </Button>
                  )}
                  {auto.channel === 'voice' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Anteprima prompt voce"
                      onClick={() => openPreviewPrompt(auto)}
                    >
                      <FileText className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" />
                    </Button>
                  )}
                  {auto.channel !== 'voice' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="Anteprima messaggio"
                      onClick={() => openPreviewPrompt(auto)}
                    >
                      <FileText className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(auto)}>
                    <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(auto.id)}>
                    <Trash2 className="w-3.5 h-3.5 text-red-400 dark:text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-background border-border text-foreground max-w-lg max-h-[85vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-500 dark:text-amber-400" />
              {editingId ? 'Modifica Automazione' : 'Nuova Automazione'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Configura l'invio automatico di messaggi su eventi appuntamento
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-foreground">Nome automazione *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Es: Conferma appuntamento via WhatsApp"
                className="bg-background border-input h-8 text-sm"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground">Evento trigger</Label>
                <Select value={form.trigger_event} onValueChange={(v) => setForm({ ...form, trigger_event: v })}>
                  <SelectTrigger className="bg-background border-input h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created">Appuntamento creato</SelectItem>
                    <SelectItem value="status_changed">Cambio stato</SelectItem>
                    <SelectItem value="rescheduled">Riprogrammato</SelectItem>
                    <SelectItem value="reminder">Promemoria</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-foreground">Canale</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v, sender_id: '', sender_display: '' })}>
                  <SelectTrigger className="bg-background border-input h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="email">Email (SMTP)</SelectItem>
                    <SelectItem value="gmail">Email (Gmail)</SelectItem>
                    <SelectItem value="voice">Chiamata vocale AI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {(form.channel === 'whatsapp' || form.channel === 'sms') && (
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground">
                  Sender {form.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}
                </Label>
                <Select
                  value={form.sender_id || ''}
                  onValueChange={(v) => {
                    const s = senders.find(s => s.id === v);
                    setForm({
                      ...form,
                      sender_id: v,
                      sender_display: s ? `${s.friendly_name || ''} ${s.phone_number}`.trim() : v,
                    });
                  }}
                >
                  <SelectTrigger className="bg-background border-input h-8 text-sm">
                    <SelectValue placeholder={`Seleziona sender ${form.channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}...`} />
                  </SelectTrigger>
                  <SelectContent>
                    {senders
                      .filter(s => s.channel === form.channel)
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          <div className="flex items-center gap-2">
                            {s.friendly_name && <span className="font-medium">{s.friendly_name}</span>}
                            <span className={s.friendly_name ? 'text-muted-foreground' : ''}>{s.phone_number}</span>
                            {s.is_default && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 ml-1">Default</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {(form.channel === 'email' || form.channel === 'gmail') && (
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground">
                  Account {form.channel === 'gmail' ? 'Gmail' : 'SMTP'} mittente
                </Label>
                <Select
                  value={form.sender_id || ''}
                  onValueChange={(v) => {
                    const ea = emailAccounts.find(a => a.id === v);
                    setForm({
                      ...form,
                      sender_id: v,
                      sender_display: ea ? `${ea.display_name || ''} <${ea.email_address}>`.trim() : v,
                    });
                  }}
                >
                  <SelectTrigger className="bg-background border-input h-8 text-sm">
                    <SelectValue placeholder={form.channel === 'gmail' ? 'Seleziona account Gmail...' : 'Seleziona account SMTP...'} />
                  </SelectTrigger>
                  <SelectContent>
                    {emailAccounts
                      .filter(ea => form.channel === 'gmail' ? ea.account_type === 'gmail' : ea.account_type !== 'gmail')
                      .map((ea) => (
                      <SelectItem key={ea.id} value={ea.id}>
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3 text-purple-500 dark:text-purple-400 shrink-0" />
                          {ea.display_name && <span className="font-medium">{ea.display_name}</span>}
                          <span className={ea.display_name ? 'text-muted-foreground text-xs' : ''}>{ea.email_address}</span>
                          <Badge variant="outline" className="text-[9px] h-4 px-1 ml-1">
                            {ea.account_type === 'gmail' ? 'Gmail' : 'SMTP'}
                          </Badge>
                          {ea.is_default && (
                            <Badge variant="outline" className="text-[9px] h-4 px-1 ml-1 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400">Default</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.trigger_event === 'status_changed' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground">Stato target</Label>
                <Select value={form.trigger_status} onValueChange={(v) => setForm({ ...form, trigger_status: v })}>
                  <SelectTrigger className="bg-background border-input h-8 text-sm">
                    <SelectValue placeholder="Qualsiasi stato" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confermato</SelectItem>
                    <SelectItem value="cancelled">Annullato</SelectItem>
                    <SelectItem value="completed">Completato</SelectItem>
                    <SelectItem value="no_show">Non presentato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {form.trigger_event === 'reminder' && (
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground">Ore prima dell'appuntamento</Label>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={form.reminder_hours_before ?? ''}
                  onChange={(e) => setForm({ ...form, reminder_hours_before: e.target.value === '' ? null : Number(e.target.value) })}
                  className="bg-background border-input h-8 text-sm"
                />
                <p className="text-xs text-muted-foreground">Unità: ore. 0 = all’inizio; 0,5 = 30 minuti; vuoto = nessun anticipo configurato.</p>
              </div>
            )}

            <div className="space-y-1.5">
              <Label className="text-xs text-foreground">Tipo appuntamento (opzionale)</Label>
              <Select value={form.service_type_id} onValueChange={(v) => setForm({ ...form, service_type_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="bg-background border-input h-8 text-sm">
                  <SelectValue placeholder="Tutti i tipi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  {serviceTypes.filter(t => t.is_active).map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: type.color }} />
                        {type.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {form.channel === 'whatsapp' && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-foreground">Template WhatsApp (approvato)</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px] border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                      onClick={openMessagePreview}
                      disabled={!form.whatsapp_template_id && !form.template_id}
                    >
                      <Eye className="w-3 h-3 mr-1" /> Anteprima
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px] border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                      onClick={handleSendTestMessage}
                      disabled={sendingTestMessage || !editingId || (!form.whatsapp_template_id && !form.template_id)}
                      title={editingId ? 'Invia un test reale al tuo numero' : 'Salva l\'automazione per abilitare il test'}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {sendingTestMessage ? 'Invio…' : 'Invia test a me'}
                    </Button>
                  </div>
                </div>
                <Select
                  value={form.whatsapp_template_id || ''}
                  onValueChange={(v) => {
                    const tmpl = waTemplates.find(t => t.id === v);
                    setForm({
                      ...form,
                      whatsapp_template_id: v,
                      template_id: tmpl?.content_sid || '',
                      template_name: tmpl?.name || tmpl?.friendly_name || '',
                    });
                  }}
                >
                  <SelectTrigger className="bg-background border-input h-8 text-sm">
                    <SelectValue placeholder="Seleziona template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {waTemplates.length === 0 ? (
                      <SelectItem value="__none" disabled>Nessun template approvato</SelectItem>
                    ) : (
                      waTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-3 h-3 text-green-500 dark:text-green-400 shrink-0" />
                            <span className="truncate">{t.name}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {form.template_id && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    SID: {form.template_id}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground mt-1">
                  Variabili disponibili nel template: {'{{customer_name}}'}, {'{{customer_phone}}'}, {'{{appointment_date}}'}, {'{{appointment_time}}'}, {'{{appointment_datetime}}'}, {'{{service_name}}'}, {'{{service_duration}}'}, {'{{operator_name}}'}, {'{{store_name}}'}, {'{{store_address}}'}, {'{{reschedule_window}}'}, {'{{cancellation_policy}}'}, {'{{cancel_link}}'}, {'{{reschedule_link}}'}.
                </p>
              </div>
            )}

            {(form.channel === 'email' || form.channel === 'gmail') && (
              <>
                <div className="flex items-center justify-between gap-3 p-2 bg-muted rounded-lg border border-border">
                  <div>
                    <Label className="text-xs text-foreground">Allega invito calendario (.ics)</Label>
                    <p className="text-[10px] text-muted-foreground">Il destinatario può aggiungere l'evento al proprio calendario con un click.</p>
                  </div>
                  <Switch
                    checked={form.attach_ics}
                    onCheckedChange={(checked) => setForm({ ...form, attach_ics: checked })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-foreground">Destinatario</Label>
                  <Select value={form.recipient} onValueChange={(v) => setForm({ ...form, recipient: v })}>
                    <SelectTrigger className="bg-background border-input h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Cliente (email appuntamento)</SelectItem>
                      <SelectItem value="consultant">Consulente assegnato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {form.channel === 'voice' && (
              <div className="space-y-3 p-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-950/40">
                <div className="flex items-start gap-2 text-[11px] text-violet-800 dark:text-violet-200">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    L'agente AI chiamerà il cliente al numero registrato sull'appuntamento. Verifica che l'opt-in
                    al contatto telefonico sia stato raccolto e rispettato (GDPR/normativa locale).
                  </span>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-foreground">Agente AI</Label>
                  <Select value={form.voice_agent_id} onValueChange={(v) => setForm({ ...form, voice_agent_id: v })}>
                    <SelectTrigger className="bg-background border-input h-8 text-sm">
                      <SelectValue placeholder={voiceAgents.length === 0 ? 'Nessun agente AI configurato — creane uno in AI → Agenti' : 'Seleziona un agente AI'} />
                    </SelectTrigger>
                    <SelectContent>
                      {voiceAgents.length === 0 ? (
                        <SelectItem value="__none" disabled>Nessun agente AI configurato</SelectItem>
                      ) : voiceAgents.map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          <div className="flex items-center gap-2">
                            <Mic className="w-3 h-3 text-violet-500 dark:text-violet-400" />
                            <span>{a.name}</span>
                            {a.provider_name && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300">
                                {a.provider_name}
                              </Badge>
                            )}
                            {a.is_active === false && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 border-input text-muted-foreground">
                                Inattivo
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">
                    Il provider voce viene determinato automaticamente dall'agente selezionato.
                    {voiceAgents.length === 0 && ' Crea un agente nella sezione AI → Agenti per usare questo canale.'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-foreground">Trunk SIP outbound</Label>
                    <Select value={form.voice_trunk_id} onValueChange={(v) => setForm({ ...form, voice_trunk_id: v })}>
                      <SelectTrigger className="bg-background border-input h-8 text-sm">
                        <SelectValue placeholder={voiceTrunks.length === 0 ? 'Nessun trunk configurato' : 'Trunk default tenant'} />
                      </SelectTrigger>
                      <SelectContent>
                        {voiceTrunks.length === 0 ? (
                          <SelectItem value="__none" disabled>Nessun trunk configurato</SelectItem>
                        ) : voiceTrunks.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            <div className="flex items-center gap-2">
                              <Phone className="w-3 h-3 text-violet-500 dark:text-violet-400" />
                              <span>{t.name}</span>
                              {t.is_default && <Badge variant="outline" className="text-[9px] h-4 px-1">Default</Badge>}
                              {t.is_active === false && (
                                <Badge variant="outline" className="text-[9px] h-4 px-1 border-input text-muted-foreground">
                                  Inattivo
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-foreground">Lingua</Label>
                    <Select value={form.voice_language} onValueChange={(v) => setForm({ ...form, voice_language: v })}>
                      <SelectTrigger className="bg-background border-input h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="it">Italiano</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs text-foreground">Istruzioni vocali aggiuntive (opzionale)</Label>
                  <Textarea
                    value={form.voice_script}
                    onChange={(e) => setForm({ ...form, voice_script: e.target.value })}
                    placeholder="Es. Buongiorno {{customer_name}}, la chiamo per l'appuntamento del {{appointment_date}} alle {{appointment_time}}…"
                    className="bg-background border-input text-sm min-h-[100px] resize-none"
                    rows={4}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Questo è solo uno script breve che si aggiunge al prompt nativo, già molto dettagliato (ruolo,
                    obiettivo per trigger, identificazione cliente, stato appuntamento, catalogo servizi/risorse,
                    playbook MCP, libreria obiezioni, edge case, GDPR, chiusura). La conferma, l'annullamento e
                    lo spostamento sono gestiti in modo conversazionale tramite gli MCP tools.
                    {' '}Variabili disponibili: {'{{customer_name}}'}, {'{{appointment_date}}'}, {'{{appointment_time}}'}, {'{{appointment_datetime}}'}, {'{{service_name}}'}, {'{{service_duration}}'}, {'{{operator_name}}'}, {'{{store_name}}'}, {'{{store_address}}'}, {'{{reschedule_window}}'}, {'{{cancellation_policy}}'}.
                  </p>
                </div>

                <div className="space-y-1.5 max-w-[200px]">
                  <Label className="text-xs text-foreground">Tentativi massimi</Label>
                  <Input
                    type="number"
                    min={1}
                    max={5}
                    value={form.voice_max_attempts}
                    onChange={(e) => setForm({ ...form, voice_max_attempts: parseInt(e.target.value) || 2 })}
                    className="bg-background border-input h-8 text-sm"
                  />
                  <p className="text-[10px] text-muted-foreground">Quante volte richiamare se il cliente non risponde o è occupato.</p>
                </div>
              </div>
            )}

            {(form.channel === 'sms' || form.channel === 'email' || form.channel === 'gmail') && (
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-2 bg-muted rounded-lg border border-border">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, use_template: true, message_body: '', email_subject: '' })}
                    className={`flex-1 text-xs py-1.5 px-3 rounded-md font-medium transition-colors ${form.use_template ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Da Template
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, use_template: false, template_id: '', template_name: '' })}
                    className={`flex-1 text-xs py-1.5 px-3 rounded-md font-medium transition-colors ${!form.use_template ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Testo Libero
                  </button>
                </div>
              </div>
            )}

            {form.channel === 'sms' && form.use_template && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-foreground">Template SMS</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px] border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                      onClick={openMessagePreview}
                      disabled={!form.template_id && !form.message_body}
                    >
                      <Eye className="w-3 h-3 mr-1" /> Anteprima
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px] border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                      onClick={handleSendTestMessage}
                      disabled={sendingTestMessage || !editingId || (!form.template_id && !form.message_body)}
                      title={editingId ? 'Invia un test reale al tuo numero' : 'Salva l\'automazione per abilitare il test'}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {sendingTestMessage ? 'Invio…' : 'Invia test a me'}
                    </Button>
                  </div>
                </div>
                <Select
                  value={form.template_id || ''}
                  onValueChange={(v) => {
                    const tmpl = waTemplates.find(t => t.content_sid === v);
                    setForm({
                      ...form,
                      template_id: v,
                      template_name: tmpl?.name || tmpl?.friendly_name || '',
                      message_body: tmpl?.body_text || '',
                    });
                  }}
                >
                  <SelectTrigger className="bg-background border-input h-8 text-sm">
                    <SelectValue placeholder="Seleziona template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {waTemplates.length === 0 ? (
                      <SelectItem value="__none" disabled>Nessun template disponibile</SelectItem>
                    ) : (
                      waTemplates.map((t) => (
                        <SelectItem key={t.content_sid} value={t.content_sid}>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-3 h-3 text-blue-500 dark:text-blue-400 shrink-0" />
                            <span className="truncate">{t.name}</span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {form.message_body && (
                  <p className="text-[10px] text-muted-foreground mt-1 line-clamp-2">
                    Anteprima: {form.message_body}
                  </p>
                )}
              </div>
            )}

            {form.channel === 'sms' && !form.use_template && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-foreground">Testo SMS</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px] border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                      onClick={openMessagePreview}
                      disabled={!form.message_body}
                    >
                      <Eye className="w-3 h-3 mr-1" /> Anteprima
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px] border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                      onClick={handleSendTestMessage}
                      disabled={sendingTestMessage || !editingId || !form.message_body}
                      title={editingId ? 'Invia un test reale al tuo numero' : 'Salva l\'automazione per abilitare il test'}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {sendingTestMessage ? 'Invio…' : 'Invia test a me'}
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={form.message_body}
                  onChange={(e) => setForm({ ...form, message_body: e.target.value })}
                  placeholder="Scrivi il messaggio SMS... Usa {{customer_name}}, {{appointment_date}}, {{appointment_time}} per variabili dinamiche"
                  className="bg-background border-input text-sm min-h-[80px] resize-none"
                  rows={3}
                />
                <p className="text-[10px] text-muted-foreground">
                  Variabili: {'{{customer_name}}'}, {'{{appointment_date}}'}, {'{{appointment_time}}'}, {'{{appointment_datetime}}'}, {'{{service_type}}'}, {'{{service_name}}'}, {'{{service_duration}}'}, {'{{operator_name}}'}, {'{{store_name}}'}, {'{{store_address}}'}, {'{{reschedule_window}}'}, {'{{cancellation_policy}}'}, {'{{cancel_link}}'}, {'{{reschedule_link}}'}.
                </p>
              </div>
            )}

            {(form.channel === 'email' || form.channel === 'gmail') && form.use_template && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-foreground">Template Email</Label>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px] border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                      onClick={openMessagePreview}
                      disabled={!form.template_id}
                    >
                      <Eye className="w-3 h-3 mr-1" /> Anteprima
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px] border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                      onClick={handleSendTestMessage}
                      disabled={sendingTestMessage || !editingId || !form.template_id}
                      title={editingId ? 'Invia un test reale alla tua email' : 'Salva l\'automazione per abilitare il test'}
                    >
                      <Send className="w-3 h-3 mr-1" />
                      {sendingTestMessage ? 'Invio…' : 'Invia test a me'}
                    </Button>
                  </div>
                </div>
                <Select
                  value={form.template_id || ''}
                  onValueChange={(v) => {
                    const tmpl = emailTemplates.find(t => t.id === v);
                    setForm({
                      ...form,
                      template_id: v,
                      template_name: tmpl?.name || '',
                    });
                  }}
                >
                  <SelectTrigger className="bg-background border-input h-8 text-sm">
                    <SelectValue placeholder="Seleziona template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {emailTemplates.length === 0 ? (
                      <SelectItem value="__none" disabled>Nessun template email</SelectItem>
                    ) : (
                      emailTemplates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <div className="flex items-center gap-2">
                            <Mail className="w-3 h-3 text-purple-500 dark:text-purple-400 shrink-0" />
                            <span className="truncate">{t.name}</span>
                            {t.is_default && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 ml-1">Default</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {form.template_name && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Oggetto: {emailTemplates.find(t => t.id === form.template_id)?.subject || ''}
                  </p>
                )}
              </div>
            )}

            {(form.channel === 'email' || form.channel === 'gmail') && !form.use_template && (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-foreground">Oggetto Email</Label>
                  <Input
                    value={form.email_subject}
                    onChange={(e) => setForm({ ...form, email_subject: e.target.value })}
                    placeholder="Es: Conferma appuntamento del {{appointment_date}}"
                    className="bg-background border-input h-8 text-sm"
                  />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-foreground">Corpo Email</Label>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[11px] border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950/40"
                        onClick={openMessagePreview}
                        disabled={!form.message_body && !form.email_subject}
                      >
                        <Eye className="w-3 h-3 mr-1" /> Anteprima
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 px-2 text-[11px] border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                        onClick={handleSendTestMessage}
                        disabled={sendingTestMessage || !editingId || (!form.message_body && !form.email_subject)}
                        title={editingId ? 'Invia un test reale alla tua email' : 'Salva l\'automazione per abilitare il test'}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        {sendingTestMessage ? 'Invio…' : 'Invia test a me'}
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={form.message_body}
                    onChange={(e) => setForm({ ...form, message_body: e.target.value })}
                    placeholder="Scrivi il contenuto dell'email... Usa {{customer_name}}, {{appointment_date}}, {{appointment_time}} per variabili dinamiche"
                    className="bg-background border-input text-sm min-h-[100px] resize-none"
                    rows={4}
                  />
                  <p className="text-[10px] text-muted-foreground">
                    Variabili: {'{{customer_name}}'}, {'{{customer_email}}'}, {'{{appointment_date}}'}, {'{{appointment_time}}'}, {'{{appointment_datetime}}'}, {'{{service_type}}'}, {'{{service_name}}'}, {'{{service_duration}}'}, {'{{operator_name}}'}, {'{{store_name}}'}, {'{{store_address}}'}, {'{{reschedule_window}}'}, {'{{cancellation_policy}}'}, {'{{cancel_link}}'}, {'{{reschedule_link}}'}.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground">Ritardo (minuti)</Label>
                <Input
                  type="number"
                  min={0}
                  max={1440}
                  value={form.delay_minutes}
                  onChange={(e) => setForm({ ...form, delay_minutes: parseInt(e.target.value) || 0 })}
                  className="bg-background border-input h-8 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-foreground">Priorità</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                  className="bg-background border-input h-8 text-sm"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              />
              <Label className="text-xs text-foreground">Attiva</Label>
            </div>

            {testStatus && (
              <div className="pt-1">{renderTestStatusBadge()}</div>
            )}
          </div>

          <DialogFooter className="pt-4 border-t border-border">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-input text-muted-foreground h-8 text-sm">
              Annulla
            </Button>
            <Button className="h-8 text-sm" onClick={handleSave}>
              {editingId ? 'Salva Modifiche' : 'Crea Automazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showVoiceWarning} onOpenChange={(open) => { if (!open) { setShowVoiceWarning(false); setPendingSavePayload(null); } }}>
        <DialogContent className="bg-background max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <AlertCircle className="w-5 h-5" />
              Attivare chiamate vocali AI
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground pt-2 space-y-2">
              <p>
                Stai per attivare un'automazione che farà partire <strong>chiamate telefoniche reali</strong>
                ai tuoi clienti tramite agente AI. Le chiamate consumano credito SIP e il consenso al
                contatto telefonico (opt-in) è obbligatorio per legge in molti paesi (es. GDPR in UE,
                TCPA negli USA).
              </p>
              <p>
                Assicurati che:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>I clienti abbiano dato consenso esplicito ad essere chiamati</li>
                <li>Lo script vocale identifichi chi chiama e perché</li>
                <li>L'orario di esecuzione rispetti i limiti di legge (no chiamate notturne)</li>
                <li>Hai testato lo script con il pulsante "test" sul tuo numero personale</li>
              </ul>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVoiceWarning(false); setPendingSavePayload(null); }} className="h-8 text-sm">
              Annulla
            </Button>
            <Button onClick={confirmVoiceOptin} className="h-8 text-sm">
              Ho compreso, attiva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!previewAuto}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewAuto(null);
            setPreviewResult(null);
            setPreviewError(null);
          }
        }}
      >
        <DialogContent className="bg-background border-border text-foreground max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-500 dark:text-violet-400" />
              {previewAuto && previewAuto.channel === 'voice' ? 'Anteprima prompt voce' : 'Anteprima messaggio'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              {previewAuto ? (
                <>Automazione: <strong className="text-foreground">{previewAuto.name}</strong> · canale <strong className="text-foreground">{channelLabels[previewAuto.channel] || previewAuto.channel}</strong> · trigger <strong className="text-foreground">{triggerLabels[previewAuto.trigger_event] || previewAuto.trigger_event}</strong></>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="grid grid-cols-1 gap-2">
              <Label className="text-xs text-foreground">Appuntamento di esempio</Label>
              <Select
                value={previewSelectedId}
                onValueChange={(v) => { setPreviewSelectedId(v); setPreviewManualId(''); }}
                disabled={previewLoadingAppts || previewAppointments.length === 0}
              >
                <SelectTrigger className="bg-background border-input h-8 text-sm">
                  <SelectValue placeholder={
                    previewLoadingAppts
                      ? 'Caricamento appuntamenti…'
                      : previewAppointments.length === 0
                        ? 'Nessun appuntamento recente'
                        : 'Seleziona un appuntamento'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {previewAppointments.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="text-xs">{formatPreviewAppointmentLabel(a)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-muted-foreground shrink-0">oppure ID manuale:</span>
                <Input
                  value={previewManualId}
                  onChange={(e) => setPreviewManualId(e.target.value)}
                  placeholder="UUID appuntamento"
                  className="bg-background border-input h-7 text-xs flex-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={runPreviewPrompt}
                disabled={previewLoadingPrompt || (!previewSelectedId && !previewManualId.trim())}
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${previewLoadingPrompt ? 'animate-spin' : ''}`} />
                {previewLoadingPrompt ? 'Generazione…' : 'Genera anteprima'}
              </Button>
              {previewResult && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs border-input"
                  onClick={copyPreviewPrompt}
                >
                  <Copy className="w-3.5 h-3.5 mr-1" />
                  Copia
                </Button>
              )}
            </div>

            {previewError && (
              <div className="p-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{previewError}</span>
              </div>
            )}

            {previewResult && previewResult.kind === 'voice' && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300">
                    Lunghezza: {previewResult.length}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300">
                    Trigger: {triggerLabels[previewResult.trigger] || previewResult.trigger}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300">
                    Lingua: {previewResult.language}
                  </Badge>
                  {previewResult.hash && (
                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-input text-muted-foreground font-mono">
                      hash: {previewResult.hash.slice(0, 12)}…
                    </Badge>
                  )}
                </div>
                <Textarea
                  value={previewResult.prompt}
                  readOnly
                  className="bg-muted border-border text-xs font-mono min-h-[320px] max-h-[420px] resize-y"
                />
              </div>
            )}

            {previewResult && previewResult.kind === 'message' && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300">
                    Canale: {channelLabels[previewResult.channel] || previewResult.channel}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300">
                    Lunghezza: {previewResult.length}
                  </Badge>
                  {previewResult.template_name && (
                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300">
                      Template: {previewResult.template_name}
                    </Badge>
                  )}
                  {previewResult.has_ics_attachment && (
                    <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300">
                      Allegato .ics
                    </Badge>
                  )}
                </div>
                {(previewResult.recipient_address || previewResult.sender_display) && (
                  <div className="text-[11px] text-muted-foreground space-y-0.5 p-2 rounded border border-border bg-muted">
                    {previewResult.sender_display && (
                      <div><span className="text-muted-foreground">Da:</span> <span className="font-mono">{previewResult.sender_display}</span></div>
                    )}
                    {previewResult.recipient_address && (
                      <div>
                        <span className="text-muted-foreground">A ({previewResult.recipient === 'consultant' ? 'consulente' : 'cliente'}):</span>{' '}
                        <span className="font-mono">{previewResult.recipient_address}</span>
                      </div>
                    )}
                  </div>
                )}
                {previewResult.subject !== null && (
                  <div className="space-y-1">
                    <Label className="text-xs text-foreground">Oggetto</Label>
                    <Input value={previewResult.subject || ''} readOnly className="bg-muted border-border text-sm" />
                  </div>
                )}
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-foreground">Corpo del messaggio</Label>
                    {previewResult.body_html && (
                      <div className="flex items-center gap-1 p-0.5 rounded-md border border-border bg-muted">
                        <button
                          type="button"
                          onClick={() => setPreviewBodyMode('rendered')}
                          className={`text-[10px] px-2 py-0.5 rounded ${previewBodyMode === 'rendered' ? 'bg-background shadow-sm text-violet-700 dark:text-violet-300 font-medium' : 'text-muted-foreground'}`}
                        >
                          Anteprima
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewBodyMode('source')}
                          className={`text-[10px] px-2 py-0.5 rounded ${previewBodyMode === 'source' ? 'bg-background shadow-sm text-violet-700 dark:text-violet-300 font-medium' : 'text-muted-foreground'}`}
                        >
                          HTML
                        </button>
                      </div>
                    )}
                  </div>
                  {previewResult.body_html && previewBodyMode === 'rendered' ? (
                    <div
                      className="bg-background border border-border rounded-md p-3 text-sm min-h-[260px] max-h-[420px] overflow-auto prose prose-sm max-w-none"
                      // Body HTML mixes admin-authored template with server-resolved
                      // variables that may include attacker-controlled customer data
                      // (e.g. customer_name from public booking form). Sanitize with
                      // DOMPurify before rendering to prevent stored XSS in admin UI.
                      // eslint-disable-next-line react/no-danger -- legacy guardrail #1219
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewResult.body_html, {
                        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'meta', 'link'],
                        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'formaction', 'srcdoc'],
                      }) }}
                    />
                  ) : (
                    <Textarea
                      value={previewResult.body_html && previewBodyMode === 'source' ? previewResult.body_html : previewResult.body}
                      readOnly
                      className="bg-muted border-border text-xs font-mono min-h-[260px] max-h-[420px] resize-y"
                    />
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="h-8 text-sm border-input"
              onClick={() => { setPreviewAuto(null); setPreviewResult(null); setPreviewError(null); }}
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #397: anteprima messaggio renderizzato */}
      <Dialog
        open={msgPreviewOpen}
        onOpenChange={(open) => {
          if (!open) {
            setMsgPreviewOpen(false);
            setMsgPreviewResult(null);
            setMsgPreviewError(null);
          }
        }}
      >
        <DialogContent className="bg-background border-border text-foreground max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-500 dark:text-purple-400" />
              Anteprima messaggio
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              Le variabili vengono sostituite usando un appuntamento reale (o uno di esempio se non disponibile).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-foreground">Appuntamento di esempio</Label>
              <Select
                value={msgPreviewSelectedId || '__none'}
                onValueChange={(v) => {
                  const id = v === '__none' ? '' : v;
                  setMsgPreviewSelectedId(id);
                  runMessagePreview(id);
                }}
                disabled={msgPreviewLoadingAppts}
              >
                <SelectTrigger className="bg-background border-input h-8 text-sm">
                  <SelectValue placeholder={
                    msgPreviewLoadingAppts ? 'Caricamento appuntamenti…' : 'Seleziona un appuntamento'
                  } />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">
                    <span className="text-xs italic text-muted-foreground">Appuntamento fittizio (Mario Rossi)</span>
                  </SelectItem>
                  {msgPreviewAppts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="text-xs">{formatPreviewAppointmentLabel(a)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => runMessagePreview()}
                disabled={msgPreviewLoading}
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1 ${msgPreviewLoading ? 'animate-spin' : ''}`} />
                {msgPreviewLoading ? 'Generazione…' : 'Aggiorna anteprima'}
              </Button>
              {msgPreviewResult?.is_fictitious && (
                <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40">
                  Dati fittizi
                </Badge>
              )}
            </div>

            {msgPreviewError && (
              <div className="p-2 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/40 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>{msgPreviewError}</span>
              </div>
            )}

            {msgPreviewResult && (
              <div className="space-y-3">
                {msgPreviewResult.unresolved.length > 0 && (
                  <div className="p-2 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 text-xs text-amber-800 dark:text-amber-200">
                    <div className="flex items-center gap-1.5 mb-1 font-medium">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Variabili non risolte ({msgPreviewResult.unresolved.length})
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {msgPreviewResult.unresolved.map((u) => (
                        <Badge key={u} variant="outline" className="text-[10px] h-5 px-1.5 border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 bg-background font-mono">
                          {`{{${u}}}`}
                        </Badge>
                      ))}
                    </div>
                    <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1">
                      Verranno mostrate al cliente come testo letterale a meno di non rimuoverle.
                    </p>
                  </div>
                )}

                {msgPreviewResult.subject !== null && (
                  <div>
                    <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Oggetto</Label>
                    <div className="mt-1 p-2 rounded border border-border bg-muted text-sm">
                      {renderHighlightedPreview(msgPreviewResult.subject || '', msgPreviewResult.unresolved)}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Corpo</Label>
                  <div className="mt-1 p-3 rounded border border-border bg-muted text-sm whitespace-pre-wrap break-words min-h-[120px]">
                    {renderHighlightedPreview(msgPreviewResult.body, msgPreviewResult.unresolved)}
                  </div>
                </div>

                {msgPreviewResult.appointment.id && !msgPreviewResult.is_fictitious && (
                  <div className="text-[10px] text-muted-foreground">
                    Basato su appuntamento: {msgPreviewResult.appointment.customer_name || 'cliente'}
                    {msgPreviewResult.appointment.start_time
                      ? ` · ${new Date(msgPreviewResult.appointment.start_time).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                      : ''}
                  </div>
                )}
              </div>
            )}

            {/* Task #425: mini-cronologia degli ultimi invii di test (collapsible) */}
            {testSendsAutomationId && (
              <Collapsible open={testSendsOpen} onOpenChange={setTestSendsOpen}>
                <div className="border border-border rounded-md bg-card">
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <span className="flex items-center gap-1.5">
                        {testSendsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                        <History className="w-3.5 h-3.5 text-muted-foreground" />
                        Ultimi test
                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 border-input text-muted-foreground bg-muted">
                          {testSends.length}
                        </Badge>
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (testSendsAutomationId) loadTestSends(testSendsAutomationId);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            if (testSendsAutomationId) loadTestSends(testSendsAutomationId);
                          }
                        }}
                        className="inline-flex items-center text-[10px] text-muted-foreground hover:text-foreground"
                        title="Aggiorna cronologia"
                      >
                        <RefreshCw className={`w-3 h-3 ${testSendsLoading ? 'animate-spin' : ''}`} />
                      </span>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border-t border-border">
                      {testSendsLoading && testSends.length === 0 ? (
                        <div className="px-3 py-3 text-[11px] text-muted-foreground italic">Caricamento…</div>
                      ) : testSends.length === 0 ? (
                        <div className="px-3 py-3 text-[11px] text-muted-foreground italic">
                          Nessun test inviato finora. Usa "Invia test a me" per iniziare.
                        </div>
                      ) : (
                        <ul className="divide-y divide-border">
                          {testSends.map((entry) => {
                            const isOk = entry.status === 'success';
                            const ts = entry.created_at ? new Date(entry.created_at) : null;
                            const pid = entry.provider_message_id || '';
                            const isTwilioSid = /^(SM|MM|WA)[A-Za-z0-9]{32}$/.test(pid);
                            const twilioLink = isTwilioSid
                              ? `https://console.twilio.com/us1/monitor/logs/sms/${pid}`
                              : null;
                            // Task #444: badge dello stato finale di consegna
                            // ricevuto dai webhook Twilio (SMS/WhatsApp) o dal
                            // provider email transazionale (SendGrid/Mailgun)
                            // tramite POST /api/email/webhooks/email-status.
                            // Se il provider non invia callback, resta visibile
                            // solo lo stato iniziale (Inviato/Errore).
                            const deliveryStatus = entry.delivery_status || null;
                            const deliveryLabels: Record<string, string> = {
                              queued: 'In coda',
                              sending: 'In invio',
                              sent: 'Accettato (consegna non confermata)',
                              delivered: 'Consegnato',
                              read: 'Letto',
                              failed: 'Fallito',
                              undelivered: 'Non recapitato',
                            };
                            const deliveryClass: Record<string, string> = {
                              queued: 'border-input text-foreground bg-muted',
                              sending: 'border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40',
                              sent: 'border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/40',
                              delivered: 'border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40',
                              read: 'border-emerald-400 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 bg-emerald-100 dark:bg-emerald-900/40',
                              failed: 'border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40',
                              undelivered: 'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40',
                            };
                            const deliveryUpdatedAt = entry.delivery_status_updated_at
                              ? new Date(entry.delivery_status_updated_at)
                              : null;
                            return (
                              <li key={entry.id} className="px-3 py-2 text-[11px] flex items-start gap-2">
                                {isOk ? (
                                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                                ) : (
                                  <XCircle className="w-3.5 h-3.5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <Badge
                                      variant="outline"
                                      className={`text-[10px] h-4 px-1.5 ${isOk ? 'border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-950/40' : 'border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40'}`}
                                    >
                                      {isOk ? 'OK' : 'Errore'}
                                    </Badge>
                                    <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-input text-muted-foreground bg-muted">
                                      {channelLabels[entry.channel] || entry.channel}
                                    </Badge>
                                    {/* Task #444: stato finale di consegna ricevuto dal webhook */}
                                    {isOk && deliveryStatus && (
                                      <Badge
                                        variant="outline"
                                        className={`text-[10px] h-4 px-1.5 ${deliveryClass[deliveryStatus] || 'border-input text-muted-foreground bg-muted'}`}
                                        title={
                                          (deliveryUpdatedAt
                                            ? `Aggiornato: ${deliveryUpdatedAt.toLocaleString('it-IT')}`
                                            : '') +
                                          (entry.delivery_error_message
                                            ? `\n${entry.delivery_error_message}`
                                            : '')
                                        }
                                      >
                                        {deliveryLabels[deliveryStatus] || deliveryStatus}
                                      </Badge>
                                    )}
                                    {isOk && !deliveryStatus && entry.provider_message_id && (
                                      <Badge
                                        variant="outline"
                                        className="text-[10px] h-4 px-1.5 border-border text-muted-foreground bg-card"
                                        title="In attesa dell'aggiornamento dal provider"
                                      >
                                        In attesa…
                                      </Badge>
                                    )}
                                    <span className="font-mono text-foreground truncate" title={entry.recipient}>
                                      {entry.recipient}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5 text-[10px] text-muted-foreground flex-wrap">
                                    {ts && (
                                      <span title={ts.toLocaleString('it-IT')}>
                                        {ts.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                      </span>
                                    )}
                                    {entry.sent_by_user_name && (
                                      <span>· da {entry.sent_by_user_name}</span>
                                    )}
                                    {entry.provider_message_id && (
                                      twilioLink ? (
                                        <a
                                          href={twilioLink}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="font-mono text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
                                          title="Apri il messaggio nella console Twilio"
                                        >
                                          · {entry.provider_message_id.slice(0, 12)}…
                                        </a>
                                      ) : (
                                        <span className="font-mono text-[10px] text-muted-foreground" title={entry.provider_message_id}>
                                          · {entry.provider_message_id.slice(0, 18)}{entry.provider_message_id.length > 18 ? '…' : ''}
                                        </span>
                                      )
                                    )}
                                  </div>
                                  {!isOk && entry.error && (
                                    <div className="mt-1 text-[10px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900 rounded px-1.5 py-1 break-words">
                                      {entry.error}
                                    </div>
                                  )}
                                </div>
                                {/* Task #445: rinvia rapidamente lo stesso test */}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-6 px-2 text-[10px] border-input shrink-0"
                                  disabled={resendingEntryId === entry.id || !entry.recipient}
                                  onClick={() => resendTestFromHistory(entry)}
                                  title={`Rinvia ${channelLabels[entry.channel] || entry.channel} a ${entry.recipient}`}
                                >
                                  {resendingEntryId === entry.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin" />
                                  ) : (
                                    <Send className="w-3 h-3 mr-1" />
                                  )}
                                  {resendingEntryId === entry.id ? 'Invio…' : 'Rinvia'}
                                </Button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className="h-8 text-sm border-input"
              onClick={() => { setMsgPreviewOpen(false); setMsgPreviewResult(null); setMsgPreviewError(null); }}
            >
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task #426: dialog inline per "Invia test a me" con destinatario ricordato */}
      <Dialog
        open={testDialogOpen}
        onOpenChange={(open) => {
          if (!open && sendingTestMessage) return;
          setTestDialogOpen(open);
          if (!open) setTestDialogError(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {testDialogKind === 'email'
                ? 'Invia email di test'
                : testDialogKind === 'voice'
                  ? 'Avvia chiamata di test'
                  : 'Invia messaggio di test'}
            </DialogTitle>
            <DialogDescription>
              {testDialogKind === 'email'
                ? 'Conferma o modifica l\'indirizzo email su cui vuoi ricevere il test. L\'ultimo valore usato viene ricordato per le prossime volte.'
                : testDialogKind === 'voice'
                  ? 'Conferma o modifica il numero su cui vuoi ricevere la chiamata di test (formato internazionale, es. +393331234567). L\'ultimo valore usato viene ricordato per le prossime volte.'
                  : `Conferma o modifica il numero su cui vuoi ricevere il test ${testDialogChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'} (formato internazionale, es. +393331234567). L\'ultimo valore usato viene ricordato per le prossime volte.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="test-recipient-input" className="text-xs uppercase tracking-wide text-muted-foreground">
              {testDialogKind === 'email' ? 'Email destinatario' : 'Numero destinatario'}
            </Label>
            <Input
              id="test-recipient-input"
              type={testDialogKind === 'email' ? 'email' : 'tel'}
              value={testDialogValue}
              onChange={(e) => {
                setTestDialogValue(e.target.value);
                if (testDialogError) setTestDialogError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !sendingTestMessage) {
                  e.preventDefault();
                  submitTestMessage();
                }
              }}
              placeholder={testDialogKind === 'email' ? 'nome@azienda.com' : '+393331234567'}
              autoFocus
              disabled={sendingTestMessage}
            />
            {testDialogError && (
              <p className="text-xs text-red-600 dark:text-red-400">{testDialogError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setTestDialogOpen(false)}
              disabled={sendingTestMessage}
            >
              Annulla
            </Button>
            <Button onClick={submitTestMessage} disabled={sendingTestMessage}>
              {sendingTestMessage
                ? (testDialogKind === 'voice' ? 'Avvio…' : 'Invio…')
                : (testDialogKind === 'voice' ? 'Avvia chiamata' : 'Invia test')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}